import React from 'react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { CaretRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { SerializedTrackedVariableDto } from '../hooks/useWorkflowRun';
import type { WorkflowNode } from '../../../../types/workflow';
import { nodeLabel } from '../utils/nodeLabel';

/** Extract just the result portion from a formatted MCP response value */
function extractDisplayValue(value: string): string {
  const resultHeader = '### Result\n';
  const idx = value.indexOf(resultHeader);
  if (idx === -1) return value;
  const start = idx + resultHeader.length;
  const nextSection = value.indexOf('\n### ', start);
  const extracted = nextSection === -1 ? value.slice(start) : value.slice(start, nextSection);
  return extracted.trim();
}

const SOURCE_STYLES: Record<string, { label: string; className: string }> = {
  inputValue: { label: 'Input', className: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  macro: { label: 'Macro', className: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  extracted: { label: 'Extracted', className: 'bg-green-500/15 text-green-400 border-green-500/30' },
  credential: { label: 'Credential', className: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
  seed: { label: 'Seed', className: 'bg-teal-500/15 text-teal-400 border-teal-500/30' },
  childFlowOutput: { label: 'Called Flow', className: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  workflowInput: { label: 'Workflow Input', className: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' },
  env: { label: 'Environment', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
};

function getOriginNodeLabel(originNodeId: string | undefined, nodes: WorkflowNode[]): string | null {
  if (!originNodeId || originNodeId === 'workflow_state') return null;
  const node = nodes.find((n) => n.id === originNodeId);
  if (!node) return null;
  return nodeLabel(nodes, originNodeId);
}

export function RunVariablesPanel({
  variables,
  nodes,
}: {
  variables: SerializedTrackedVariableDto[];
  nodes: WorkflowNode[];
}) {
  const [expanded, setExpanded] = React.useState(false);

  if (variables.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1.5 w-full text-left group"
        onClick={() => setExpanded(!expanded)}
      >
        <CaretRight
          size={12}
          className={`text-muted-foreground shrink-0 transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />
        <Text weight="medium" size="sm">
          Variables
        </Text>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
          {variables.length}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-0.5">
              {variables.map((v) => {
                const style = SOURCE_STYLES[v.source] ?? {
                  label: 'Variable',
                  className: 'bg-muted text-muted-foreground border-border',
                };
                const originLabel = getOriginNodeLabel(v.originNodeId, nodes);
                const isRedacted = v.value === '[REDACTED]';
                const displayValue = isRedacted ? '[REDACTED]' : extractDisplayValue(v.value);

                return (
                  <div key={v.key} className="px-2 py-2 rounded-md hover:bg-muted/30">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-mono text-xs text-foreground truncate min-w-0">
                        ${`var.${v.key}`}
                      </span>
                      <span
                        className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${style.className}`}
                      >
                        {style.label}
                      </span>
                    </div>
                    <div
                      className={`mt-1 text-xs break-all ${isRedacted ? 'text-muted-foreground italic' : 'text-foreground'}`}
                      title={displayValue}
                    >
                      {isRedacted ? displayValue : `"${displayValue}"`}
                    </div>
                    {originLabel && (
                      <Text size="xs" className="text-muted-foreground mt-0.5">
                        from {originLabel}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Badge shown inline with a step to indicate how many variables it set */
export function StepVariablesBadge({
  variablesSet,
}: {
  variablesSet: SerializedTrackedVariableDto[];
}) {
  if (variablesSet.length === 0) return null;

  return (
    <TooltipTrigger delay={0}>
      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 cursor-default">
        {variablesSet.length} var{variablesSet.length !== 1 ? 's' : ''}
      </span>
      <Tooltip>
        <div className="space-y-0.5 max-w-xs">
          {variablesSet.map((v) => (
            <div key={v.key} className="text-xs font-mono">
              ${`var.${v.key}`} = {v.source === 'credential' ? '[REDACTED]' : `"${extractDisplayValue(v.value)}"`}
            </div>
          ))}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
}
